import {Component, OnInit, ViewChild, Input, Injector} from '@angular/core';
import {UntypedFormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {MatAutocomplete} from '@angular/material/autocomplete';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {PhoneNumberPipe} from '../../pipes/PhoneNumber.pipe';

@Component({
  selector: 'terra-phone',
  templateUrl: './phone.component.html',
  styleUrls: ['./phone.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PhoneComponent,
      multi: true
    }
  ]
})
export class PhoneComponent implements OnInit, ControlValueAccessor {

  @Input() isSubmitted = false;
  @Input() prefixWidth = '100px';
  @Input() numberWidth = 'minmax(100px, 100%)';

  private outerControl: NgControl;

  get errorState() {
    return this.outerControl.errors !== null && (!!this.outerControl.touched || this.isSubmitted);
  }

  // https://netbasal.com/angular-custom-form-controls-made-easy-4f963341c8e2

  // https://blog.angularindepth.com/never-again-be-confused-when-implementing-controlvalueaccessor-in-angular-forms-93b9eee9ee83

  // Newer: https://v9.material.angular.io/guide/creating-a-custom-form-field-control
  // And code: https://stackblitz.com/angular/yvmajbpqyed?file=src%2Fapp%2Fform-field-custom-control-example.ts

  private allPrefixes = [
    '+1', '+880', '+32', '+226', '+359', '+387', '+681', '+590', '+673', '+591', '+973', '+257', '+229', '+975', '+267', '+685',
    '+599', '+55', '+375', '+501', '+7', '+250', '+381', '+670', '+262', '+993', '+992', '+40', '+690', '+245', '+502', '+30',
    '+240', '+81', '+592', '+594', '+995', '+44', '+241', '+503', '+224', '+220', '+299', '+350', '+233', '+968', '+216', '+962',
    '+385', '+509', '+36', '+852', '+504', '+58', '+970', '+680', '+351', '+47', '+595', '+964', '+507', '+689', '+675', '+51',
    '+92', '+63', '+870', '+48', '+508', '+260', '+212', '+372', '+20', '+27', '+593', '+39', '+84', '+677', '+251', '+252', '+263',
    '+966', '+34', '+291', '+382', '+373', '+261', '+377', '+998', '+95', '+223', '+853', '+976', '+692', '+389', '+230', '+356',
    '+265', '+960', '+596', '+222', '+256', '+255', '+60', '+52', '+972', '+33', '+246', '+290', '+358', '+679', '+500', '+691',
    '+298', '+505', '+31', '+264', '+678', '+687', '+227', '+672', '+234', '+64', '+977', '+674', '+683', '+682', '+225', '+41',
    '+57', '+86', '+237', '+56', '+61', '+242', '+236', '+243', '+420', '+357', '+506', '+238', '+53', '+268', '+963', '+996', '+254',
    '+211', '+597', '+686', '+855', '+269', '+239', '+421', '+82', '+386', '+850', '+965', '+221', '+378', '+232', '+248', '+65',
    '+46', '+249', '+253', '+45', '+49', '+967', '+213', '+598', '+961', '+856', '+688', '+886', '+90', '+94', '+423', '+371', '+676',
    '+370', '+352', '+231', '+266', '+66', '+228', '+235', '+218', '+379', '+971', '+376', '+93', '+354', '+98', '+374', '+355',
    '+244', '+54', '+43', '+297', '+91', '+994', '+353', '+62', '+380', '+974', '+258'
  ];

  filteredPrefixes$: Observable<string[]>;

  prefixControl = new UntypedFormControl(null);
  phoneControl = new UntypedFormControl(null);

  @ViewChild('autocompleRef')
  autoComplete: MatAutocomplete;

  prefixControlTouched: () => any;
  phoneControlTouched: () => any;

  constructor(private _injector: Injector, private _phoneNumberPipe: PhoneNumberPipe) {
  }

  writeValue(obj: string): void {
    if (obj) {
      let splitIndex = obj.indexOf(' ');
      if (splitIndex === -1) {
        splitIndex = obj.indexOf('-');
      }

      if (splitIndex !== -1) {
        const prefixStr = obj.substring(0, splitIndex);
        const phoneStr = this._phoneNumberPipe.transform(this.removeSpacesFromPhonePart(obj.substring(splitIndex + 1)));

        const prefixIndex = this.allPrefixes.findIndex(x => x === prefixStr || x === `+${prefixStr}`);
        if (prefixIndex !== -1) {
          this.prefixControl.setValue(this.allPrefixes[prefixIndex], {
            emitEvent: false
          });
          this.phoneControl.setValue(phoneStr, {
            emitEvent: false
          });
        } else {
          this.prefixControl.reset();
          this.phoneControl.setValue(obj, {
            emitEvent: false
          });
        }
      } else {
        this.phoneControl.setValue(obj, {
          emitEvent: false
        });
      }
      this.prefixControl.updateValueAndValidity();
      this.phoneControl.updateValueAndValidity();
    }
  }

  registerOnChange(fn: any): void {
    this.prefixControl.valueChanges.subscribe(prefixValue => {
      const phoneStr = this.phoneControl.value || '';
      const combined = prefixValue && phoneStr ? prefixValue + ' ' + this.removeSpacesFromPhonePart(phoneStr) : null;
      fn(combined);
    });

    this.phoneControl.valueChanges.subscribe(phoneValue => {
      const prefixStr = this.prefixControl.value || '';
      const combined = phoneValue && prefixStr ? prefixStr + ' ' + this.removeSpacesFromPhonePart(phoneValue) : null;
      fn(combined);
    });
  }

  registerOnTouched(fn: any): void {
    this.prefixControlTouched = () => {
      fn();
    };
    this.phoneControlTouched = () => {
      fn();
    };
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.prefixControl.disable();
      this.phoneControl.disable();
    } else {
      this.prefixControl.enable();
      this.phoneControl.enable();
    }
  }

  ngOnInit() {
    this.outerControl = this._injector.get(NgControl);

    this.filteredPrefixes$ = this.prefixControl.valueChanges.pipe(
      startWith(''),
      map(str => !str ? this.allPrefixes : this.allPrefixes.filter(x => x.indexOf(str) !== -1)));
  }

  prefixClosed() {
    if (this.allPrefixes.findIndex(x => x === this.prefixControl.value) === -1) {
      this.prefixControl.setValue(null);
    }
  }

  prefixBlur() {
    if (!this.autoComplete.isOpen) {
      if (this.allPrefixes.findIndex(x => x === this.prefixControl.value) === -1) {
        this.prefixControl.setValue(null);
      }
    }
  }

  cleanPhoneString() {
    this.phoneControl.setValue(this._phoneNumberPipe.transform(this.phoneControl.value));
  }

  private removeSpacesFromPhonePart(phonePart: string) {
    return phonePart?.split(' ').join('') || null;
  }
}
